import React from 'react';
import type { OnUpdateCallbacks, PaymentMethodCreationStage, PaymentMethodSuccessResult } from './types';

import {
  isSuccessful,
  useCCPConfirmCardSetup,
  useCCPCreatePaymentMethod,
} from '@atlassian/commerce-ui/credit-card-ccp';
import { useOpenClose } from '../../../hooks/src/useOpenFalse';

export const useCCPCreditCardFormStateGenerateThenUpdateCard = ({
  updateMethod,
  onUpdateSuccess,
  onUpdateFailure: onUpdateError,
  onUpdateDone,
}: {
  updateMethod: (newPaymentMethod: string) => void;
  onUpdateDone?: () => void;
  onUpdateSuccess?: () => void;
  onUpdateFailure?: () => void;
}) => {
  const { isOpen, open, close } = useOpenClose();
  const [newPaymentMethod, setNewPaymentMethod] = React.useState<string | undefined>(undefined);
  const [stripeId, setStripeId] = React.useState<string | undefined>(undefined);
  const [creationStage, setOperationInProgress] = React.useState<PaymentMethodCreationStage>('none');
  const createPaymentMethod = useCCPCreatePaymentMethod();
  const confirm = useCCPConfirmCardSetup();

  const generatePaymentMethod = React.useCallback(async (): Promise<PaymentMethodSuccessResult | undefined> => {
    if (!createPaymentMethod) {
      return undefined;
    }

    setOperationInProgress('creation-in-progress');

    const result = await createPaymentMethod();
    setOperationInProgress('none');

    if (isSuccessful(result)) {
      setStripeId(result.payload.paymentMethod.id);
      setNewPaymentMethod(result.payload.ccpPaymentMethodId);
      open();

      return result;
    }

    return undefined;
  }, [createPaymentMethod]);

  const updatePaymentMethod = React.useCallback(
    async (stripeIdVal: string | undefined, newPaymentMethodIdVal: string | undefined) => {
      if (!confirm || !newPaymentMethodIdVal || !stripeIdVal) {
        return;
      }
      try {
        setOperationInProgress('confirmation');

        const result = await confirm(stripeIdVal);
        setOperationInProgress('none');
        if (isSuccessful(result)) {
          const updatedPaymentMethod = await updateMethod(newPaymentMethodIdVal);

          if (onUpdateSuccess) {
            onUpdateSuccess();
          }
          close();
          return updatedPaymentMethod;
        }
        if (onUpdateError) {
          onUpdateError();
        }
        close();
      } catch (err) {
        if (onUpdateError) {
          onUpdateError();
        }
        close();
      } finally {
        onUpdateDone && onUpdateDone();
      }
    },
    [confirm, onUpdateError, onUpdateSuccess, updateMethod]
  );
  const updateCard = React.useCallback(async () => {
    await updatePaymentMethod(stripeId, newPaymentMethod);
  }, [updatePaymentMethod, stripeId, newPaymentMethod]);

  // This is required for deferred confirmation (and update) of Stripe credit card
  // Is parameterized to accept external values for input params stripeId and newPaymentMethod
  const parameterizedUpdateCard = React.useCallback(
    async (stripeIdValue: string, newPaymentMethodIdValue: string) => {
      await updatePaymentMethod(stripeIdValue, newPaymentMethodIdValue);
    },
    [updatePaymentMethod]
  );

  return {
    creationStage,
    generatePaymentMethod,
    updateCard,
    parameterizedUpdateCard,
    isWarningBeingShown: isOpen,
    closeWarning: close,
    openWarning: open,
  };
};
